import * as THREE from 'three';

// Unified pointer input. Distinguishes a drag (orbit the camera) from a tap
// (raycast into the scene). Also drives an optional on-screen joystick for
// touch devices, exposing a normalized move vector.

export class InputController {
  constructor(canvas){
    this.canvas = canvas;
    this.ray = new THREE.Raycaster();
    this.ndc = new THREE.Vector2();

    // camera orbit goals (scenes read these)
    this.azimuthGoal = 0;
    this.polarGoal = 0.42;
    this.azimuthRange = [-0.85, 0.85];
    this.polarRange = [0.18, 0.8];

    // joystick move vector, range [-1,1] on each axis
    this.move = new THREE.Vector2(0, 0);
    this.moving = false;

    // driving input (Speedway) — steer + brake, independent of camera orbit
    this.steer = 0;      // -1 left .. 1 right
    this.brake = false;

    this._dragging = false;
    this._dragMoved = false;
    this._px = 0; this._py = 0;

    // tap handler set by the active scene: (ndc, ray) => void
    this.onTap = null;

    this._bindPointer();
    this._bindStick();
    this._bindKeys();
  }

  _bindPointer(){
    const c = this.canvas;
    c.addEventListener('pointerdown', e => {
      // ignore drags that start on the joystick
      if (e.target.closest && e.target.closest('.stick')) return;
      this._dragging = true; this._dragMoved = false;
      this._px = e.clientX; this._py = e.clientY;
      c.classList.add('dragging');
      c.setPointerCapture(e.pointerId);
    });
    c.addEventListener('pointermove', e => {
      if (!this._dragging) return;
      const dx = e.clientX - this._px, dy = e.clientY - this._py;
      if (Math.abs(dx) + Math.abs(dy) > 6) this._dragMoved = true;
      this._px = e.clientX; this._py = e.clientY;
      this.azimuthGoal = clamp(this.azimuthGoal - dx * 0.004, this.azimuthRange);
      this.polarGoal   = clamp(this.polarGoal   + dy * 0.003, this.polarRange);
    });
    c.addEventListener('pointerup', e => {
      this._dragging = false;
      c.classList.remove('dragging');
      if (this._dragMoved) return;              // it was a drag, not a tap
      if (!this.onTap || !this.camera) return;
      this.ndc.x =  (e.clientX / window.innerWidth)  * 2 - 1;
      this.ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
      this.ray.setFromCamera(this.ndc, this.camera);
      this.onTap(this.ndc, this.ray);
    });
  }

  _bindStick(){
    const stick = document.getElementById('stick');
    const nub = document.getElementById('stickNub');
    if (!stick) return;

    // show the joystick on any touch-capable device
    const isTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window || matchMedia('(pointer: coarse)').matches;
    if (isTouch) stick.hidden = false;

    let active = false, cx = 0, cy = 0, R = 52;
    const setNub = (dx, dy) => { nub.style.transform = `translate(${dx}px, ${dy}px)`; };
    const clampR = (dx, dy) => {
      const len = Math.hypot(dx, dy);
      if (len > R){ dx = dx / len * R; dy = dy / len * R; }
      return [dx, dy];
    };
    const update = (x, y) => {
      let dx = x - cx, dy = y - cy;
      [dx, dy] = clampR(dx, dy);
      setNub(dx, dy);
      this.move.set(dx / R, dy / R);
      this.moving = this.move.lengthSq() > 0.02;
    };
    const end = () => { active = false; this.move.set(0, 0); this.moving = false; setNub(0, 0); };

    const recenter = () => {
      const r = stick.getBoundingClientRect();
      cx = r.left + r.width / 2; cy = r.top + r.height / 2;
    };

    stick.addEventListener('pointerdown', e => {
      active = true; recenter();
      stick.setPointerCapture(e.pointerId);
      e.preventDefault(); e.stopPropagation();
    });
    stick.addEventListener('pointermove', e => {
      if (!active) return;
      e.preventDefault();
      update(e.clientX, e.clientY);
    });
    stick.addEventListener('pointerup', e => { e.preventDefault(); end(); });
    stick.addEventListener('pointercancel', e => { e.preventDefault(); end(); });

    // touch fallback for browsers that don’t fire pointer events on the stick
    stick.addEventListener('touchstart', e => {
      active = true; recenter();
      update(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault(); e.stopPropagation();
    }, { passive: false });
    stick.addEventListener('touchmove', e => {
      if (!active) return;
      update(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault();
    }, { passive: false });
    const touchEnd = e => { e.preventDefault(); end(); };
    stick.addEventListener('touchend', touchEnd, { passive: false });
    stick.addEventListener('touchcancel', touchEnd, { passive: false });
  }

  _bindKeys(){
    const left = new Set(['ArrowLeft','KeyA','KeyQ']);
    const right = new Set(['ArrowRight','KeyD','KeyE']);
    const brake = new Set(['Space','ArrowDown','KeyS']);
    window.addEventListener('keydown', e => {
      if (left.has(e.code)){ this.steer = -1; e.preventDefault(); }
      if (right.has(e.code)){ this.steer = 1; e.preventDefault(); }
      if (brake.has(e.code)){ this.brake = true; e.preventDefault(); }
    });
    window.addEventListener('keyup', e => {
      if (left.has(e.code) && this.steer === -1){ this.steer = 0; e.preventDefault(); }
      if (right.has(e.code) && this.steer === 1){ this.steer = 0; e.preventDefault(); }
      if (brake.has(e.code)){ this.brake = false; e.preventDefault(); }
    });
  }

  // scenes assign their camera so tap raycasts + orbit apply to the right one
  useCamera(cam){ this.camera = cam; }
}

function clamp(v, [lo, hi]){ return Math.max(lo, Math.min(hi, v)); }
